import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  FormFeedback,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { useNavigate, useParams } from "react-router-dom";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { imageBaseApi } from "../../../utilis/imageHelper";
import ColorPicker from "../../../Components/ColorPicker/ColorPicker";
import MediaModal from "../../PagesSection/Component/MediaModal";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import Whoweareservice from "../../../services/KangarooServices/Whoweareservice";

const AddEditWhoWeAre = () => {
  const [activeTab, setActiveTab] = useState("1");
  const [editId, setEditId] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { slug } = useParams();

  const [mediaModalState, setMediaModalState] = useState({
    isOpen: false,
    targetField: "",
    index: null,
  });

  const ValidationSchema = Yup.object().shape({
    content: Yup.string()
      .required("Content is required")
      .min(20, "Content must be at least 20 characters"),

    bannerText: Yup.string()
      .required("Banner text is required")
      .min(5, "Banner text must be at least 5 characters")
      .max(200, "Banner text must not exceed 200 characters"),

    bannerColor: Yup.string()
      .required("Banner color is required")
      .matches(/^#[0-9A-F]{6}$/i, "Must be a valid hex color code"),

    bannerImageId: Yup.string().required("Banner image is required"),

    contentImageId: Yup.string().required("Content image is required"),

    subDetails: Yup.array().of(
      Yup.object().shape({
        textDescription: Yup.string()
          .required("Description is required")
          .min(10, "Description must be at least 10 characters"),
        textNumber: Yup.number()
          .typeError("Only numbers are allowed")
          .required("Number is required")
          .min(0, "Number must be 0 or greater"),
        thumbnailId: Yup.string().required("Thumbnail image is required"),
        color: Yup.string()
          .required("Color is required")
          .matches(/^#[0-9A-F]{6}$/i, "Must be a valid hex color code"),
      })
    ),
  });

  // FORMIK SETUP
  const formik = useFormik({
    initialValues: {
      content: "",
      bannerText: "",
      bannerColor: "#000000",
      bannerImageId: "",
      bannerImage: null,
      contentMedia: null,
      contentImageId: "",
      subDetails: [],
    },
    validationSchema: ValidationSchema,
    validateOnChange: true,
    validateOnBlur: true,

    onSubmit: async (values) => {
      const payload = {
        bannerImageId: values.bannerImageId,
        bannerText: values.bannerText,
        bannerColor: values.bannerColor,
        content: values.content,
        media: values.media,
        contentImageId: values.contentImageId,
        contentMedia: values.contentMedia,
        subDetails: values.subDetails.map((detail) => ({
          image: detail.image || "",
          color: detail.color,
          thumbnailId: detail.thumbnailId,
          media: detail.media,
          orderIndex: Number(detail?.orderIndex),
          textNumber: Number(detail.textNumber),
          textDescription: detail.textDescription,
        })),
      };

      try {
        if (slug) {
          await Whoweareservice.update(editId, payload);
          toast.success("Who We Are page updated successfully");
        } else {
          await Whoweareservice.create(payload);
          toast.success("Who We Are page created successfully");
        }
        navigate("/who-we-are");
      } catch (err) {
        console.error(err);
        toast.error(err?.response?.data?.message || "Something went wrong!");
      }
    },
  });

  const {
    values,
    errors,
    touched,
    setFieldValue,
    handleChange,
    handleBlur,
    handleSubmit,
    setValues,
  } = formik;

  useEffect(() => {
    const fetchData = async () => {
      if (!slug) return;

      try {
        setLoading(true);
        const response = await Whoweareservice.getbySlug(slug);
        setEditId(response.id);
        setValues({
          content: response.content || "",
          bannerText: response.bannerText || "",
          bannerColor: response.bannerColor || "#000000",
          bannerImageId: response.bannerImageId || "",
          // media: response.media || null,
          bannerImage: response.media || null,
          contentMedia: response.contentMedia || null,
          contentImageId: response.contentImageId || "",
          subDetails:
            response.subDetails?.map((detail) => ({
              textDescription: detail.textDescription || "",
              textNumber: detail.textNumber || "",
              thumbnailId: detail.thumbnailId || "",
              orderIndex: detail?.orderIndex || 0,
              media: detail.media || null,
              image: detail.image || "",
              color: detail.color || "#000000",
            })) || [],
        });

        setLoading(false);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load data");
        setLoading(false);
        navigate("/who-we-are");
      }
    };

    fetchData();
  }, [slug]);

  // MEDIA MODAL HANDLERS
  const toggleMediaModal = (targetField = "", index = null) => {
    setMediaModalState({
      isOpen: !mediaModalState.isOpen,
      targetField,
      index,
    });
  };

  const handleMediaSelect = (media) => {
    const { targetField, index } = mediaModalState;

    if (targetField === "banner") {
      setFieldValue("bannerImageId", media.id);
      setFieldValue("bannerImage", media);
    }

    if (targetField === "content") {
      setFieldValue("contentImageId", media.id);
      setFieldValue("contentMedia", media);
    }

    if (targetField === "subDetail" && index !== null) {
      const updated = [...values.subDetails];
      updated[index].thumbnailId = media.id;
      updated[index].media = media;
      updated[index].image = media.url || "";
      setFieldValue("subDetails", updated);
    }

    toggleMediaModal();
  };

  // SUB DETAILS HANDLERS
  const addSubDetail = () => {
    setFieldValue("subDetails", [
      ...values.subDetails,
      {
        textDescription: "",
        textNumber: "",
        orderIndex: "",

        thumbnailId: "",
        media: null,
        image: "",
        color: "#000000",
      },
    ]);
  };

  const updateSubDetail = (index, field, value) => {
    const updated = [...values.subDetails];
    updated[index][field] = value;
    setFieldValue("subDetails", updated);
  };

  const removeSubDetail = (index) => {
    const updated = values.subDetails.filter((_, i) => i !== index);
    setFieldValue("subDetails", updated);
  };

  const removeSubDetailMedia = (index) => {
    const updated = [...values.subDetails];
    updated[index].thumbnailId = "";
    updated[index].media = null;
    updated[index].image = "";
    setFieldValue("subDetails", updated);
  };

  // FORM SUBMISSION WITH VALIDATION CHECK
  const handleFormSubmit = (e) => {
    e.preventDefault();

    // Mark all fields as touched
    formik.setTouched({
      content: true,
      bannerText: true,
      bannerColor: true,
      bannerImageId: true,
      contentImageId: true,
      subDetails: values.subDetails.map(() => ({
        textDescription: true,
        textNumber: true,
        orderIndex: true,

        thumbnailId: true,
        color: true,
      })),
    });

    // Check for errors
    if (Object.keys(errors).length > 0) {
      toast.error("Please fix all validation errors before submitting");

      // Navigate to first tab with errors
      if (errors.content) {
        setActiveTab("1");
      } else if (
        errors.bannerText ||
        errors.bannerImageId ||
        errors.bannerColor
      ) {
        setActiveTab("2");
      } else if (errors.subDetails) {
        setActiveTab("3");
      }
      return;
    }

    handleSubmit();
  };

  // SHOW LOADING SPINNER WHEN FETCHING DATA IN EDIT MODE
  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb pageTitle="Who We Are" title={slug ? "Edit" : "Create"} />
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "400px" }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb pageTitle="Who We Are" title={slug ? "Edit" : "Create"} />
        <form onSubmit={handleFormSubmit}>
          <div className="d-flex justify-content-end mb-3 gap-3">
            <button type="submit" className="btn btn-success">
              {slug ? "Update" : "Create"}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => navigate("/who-we-are")}
            >
              Cancel
            </button>
          </div>

          <Row>
            <Col lg={8}>
              <Card>
                <CardHeader>
                  <Nav tabs className="card-header-tabs">
                    <NavItem className="cursor-pointer">
                      <NavLink
                        className={activeTab === "1" ? "active" : ""}
                        onClick={() => setActiveTab("1")}
                      >
                        Basic Info
                        {errors.content && touched.content && (
                          <i className="ri-error-warning-line text-danger ms-1"></i>
                        )}
                      </NavLink>
                    </NavItem>

                    <NavItem className="cursor-pointer">
                      <NavLink
                        className={activeTab === "2" ? "active" : ""}
                        onClick={() => setActiveTab("2")}
                      >
                        Banner
                        {(errors.bannerText ||
                          errors.bannerImageId ||
                          errors.bannerColor) &&
                          (touched.bannerText || touched.bannerColor) && (
                            <i className="ri-error-warning-line text-danger ms-1"></i>
                          )}
                      </NavLink>
                    </NavItem>

                    <NavItem className="cursor-pointer">
                      <NavLink
                        className={activeTab === "3" ? "active" : ""}
                        onClick={() => setActiveTab("3")}
                      >
                        Sub Details
                        {errors.subDetails && (
                          <i className="ri-error-warning-line text-danger ms-1"></i>
                        )}
                      </NavLink>
                    </NavItem>
                  </Nav>
                </CardHeader>

                <CardBody>
                  <TabContent activeTab={activeTab}>
                    {/* TAB 1: Basic Info */}
                    <TabPane tabId="1">
                      <FormGroup>
                        <Label>
                          Content <span className="text-danger">*</span>
                        </Label>

                        <CKEditor
                          editor={ClassicEditor}
                          data={values.content}
                          onChange={(event, editor) => {
                            const data = editor.getData();
                            setFieldValue("content", data);
                          }}
                          onBlur={() => formik.setFieldTouched("content", true)}
                        />
                        {touched.content && errors.content && (
                          <div className="text-danger small mt-1">
                            {errors.content}
                          </div>
                        )}
                      </FormGroup>
                    </TabPane>

                    {/* TAB 2: Banner */}
                    <TabPane tabId="2">
                      <FormGroup>
                        <Label>
                          Banner Text <span className="text-danger">*</span>
                        </Label>
                        <Input
                          name="bannerText"
                          value={values.bannerText}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          invalid={touched.bannerText && !!errors.bannerText}
                        />
                        {touched.bannerText && errors.bannerText && (
                          <FormFeedback>{errors.bannerText}</FormFeedback>
                        )}
                      </FormGroup>

                      <FormGroup>
                        <Label>
                          Banner Color <span className="text-danger">*</span>
                        </Label>
                        <ColorPicker
                          value={values.bannerColor}
                          onChange={(color) =>
                            setFieldValue("bannerColor", color)
                          }
                          isToggle={false}
                        />
                        {touched.bannerColor && errors.bannerColor && (
                          <div className="text-danger small mt-1">
                            {errors.bannerColor}
                          </div>
                        )}
                      </FormGroup>

                      <FormGroup>
                        <Label className="fw-semibold mb-2">
                          Banner Image <span className="text-danger">*</span>
                        </Label>

                        <div className="text-center p-3 border rounded shadow-sm bg-light">
                          {values.bannerImage ? (
                            <div
                              style={{
                                maxHeight: "220px",
                                overflow: "hidden",
                                borderRadius: "10px",
                                border: "1px solid #ddd",
                              }}
                              className="mb-3"
                            >
                              <img
                                src={`${imageBaseApi}${values.bannerImage.url}`}
                                alt="Banner"
                                style={{ width: "100%", objectFit: "cover" }}
                                className="img-fluid hover-zoom"
                              />
                            </div>
                          ) : (
                            <div className="p-4 border rounded text-center bg-light">
                              <i className="ri-image-add-line fs-1 text-secondary"></i>
                              <h6 className="mt-2 mb-3 text-muted">
                                No banner image selected
                              </h6>
                              <h6 className="mb-2 text-muted">2400 x 400</h6>
                            </div>
                          )}

                          <Button
                            outline
                            color="secondary"
                            className="rounded-pill px-4 mt-2"
                            onClick={() => toggleMediaModal("banner")}
                          >
                            <i className="ri-image-add-line me-1"></i>{" "}
                            {values.bannerImage ? "Change" : "Choose"} Banner
                            Image
                          </Button>

                          {values.bannerImage && (
                            <Button
                              outline
                              color="danger"
                              className="rounded-pill px-4 mt-2 ms-2"
                              onClick={() => {
                                setFieldValue("bannerImageId", "");
                                setFieldValue("bannerImage", null);
                              }}
                            >
                              <i className="ri-delete-bin-5-line me-1"></i>{" "}
                              Remove
                            </Button>
                          )}
                        </div>
                        {errors.bannerImageId && (
                          <div className="text-danger small mt-1">
                            {errors.bannerImageId}
                          </div>
                        )}
                      </FormGroup>
                    </TabPane>

                    {/* TAB 3: Sub Details */}
                    <TabPane tabId="3">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <Button
                          color="success"
                          size="sm"
                          onClick={addSubDetail}
                        >
                          + Add Sub Detail
                        </Button>
                        {errors.subDetails &&
                          typeof errors.subDetails === "string" && (
                            <div className="text-danger small">
                              {errors.subDetails}
                            </div>
                          )}
                      </div>

                      <div className="mt-3 vstack gap-3">
                        {values.subDetails.map((detail, index) => (
                          <Card key={index} className="h-100">
                            <CardBody>
                              <div className="d-flex justify-content-between mb-3">
                                <h6 className="mb-0">Sub Detail {index + 1}</h6>
                                <Button
                                  color="danger"
                                  size="sm"
                                  outline
                                  onClick={() => removeSubDetail(index)}
                                >
                                  <i className="ri-delete-bin-5-line"></i>{" "}
                                  Remove
                                </Button>
                              </div>

                              <FormGroup>
                                <Label>
                                  Number <span className="text-danger">*</span>
                                </Label>
                                <Input
                                  type="number"
                                  value={detail.textNumber}
                                  onChange={(e) =>
                                    updateSubDetail(
                                      index,
                                      "textNumber",
                                      e.target.value
                                    )
                                  }
                                  onBlur={() =>
                                    formik.setFieldTouched(
                                      `subDetails[${index}].textNumber`,
                                      true
                                    )
                                  }
                                  invalid={
                                    errors.subDetails?.[index]?.textNumber &&
                                    touched.subDetails?.[index]?.textNumber
                                  }
                                />
                                {errors.subDetails?.[index]?.textNumber &&
                                  touched.subDetails?.[index]?.textNumber && (
                                    <FormFeedback>
                                      {errors.subDetails[index].textNumber}
                                    </FormFeedback>
                                  )}
                              </FormGroup>
                              <FormGroup>
                                <Label>Order Index</Label>
                                <Input
                                  type="number"
                                  value={detail.orderIndex}
                                  onChange={(e) =>
                                    updateSubDetail(
                                      index,
                                      "orderIndex",
                                      e.target.value
                                    )
                                  }
                                  onBlur={() =>
                                    formik.setFieldTouched(
                                      `subDetails[${index}].orderIndex`,
                                      true
                                    )
                                  }
                                  invalid={
                                    errors.subDetails?.[index]?.orderIndex &&
                                    touched.subDetails?.[index]?.orderIndex
                                  }
                                />
                                {errors.subDetails?.[index]?.orderIndex &&
                                  touched.subDetails?.[index]?.orderIndex && (
                                    <FormFeedback>
                                      {errors.subDetails[index].orderIndex}
                                    </FormFeedback>
                                  )}
                              </FormGroup>
                              <FormGroup>
                                <Label>
                                  Description{" "}
                                  <span className="text-danger">*</span>
                                </Label>
                                <CKEditor
                                  editor={ClassicEditor}
                                  data={detail.textDescription}
                                  onChange={(event, editor) =>
                                    updateSubDetail(
                                      index,
                                      "textDescription",
                                      editor.getData()
                                    )
                                  }
                                  onBlur={() =>
                                    formik.setFieldTouched(
                                      `subDetails[${index}].textDescription`,
                                      true
                                    )
                                  }
                                />
                                {errors.subDetails?.[index]?.textDescription &&
                                  touched.subDetails?.[index]
                                    ?.textDescription && (
                                    <div className="text-danger small mt-1">
                                      {errors.subDetails[index].textDescription}
                                    </div>
                                  )}
                              </FormGroup>

                              <FormGroup>
                                <Label>
                                  Color <span className="text-danger">*</span>
                                </Label>
                                <ColorPicker
                                  value={detail.color}
                                  onChange={(color) =>
                                    updateSubDetail(index, "color", color)
                                  }
                                  isToggle={false}
                                />
                                {errors.subDetails?.[index]?.color &&
                                  touched.subDetails?.[index]?.color && (
                                    <div className="text-danger small mt-1">
                                      {errors.subDetails[index].color}
                                    </div>
                                  )}
                              </FormGroup>

                              {/* THUMBNAIL IMAGE FIELD */}
                              <FormGroup>
                                <Label className="fw-semibold mb-2">
                                  Thumbnail Image{" "}
                                  <span className="text-danger">*</span>
                                </Label>

                                <div className="text-center p-3 border rounded shadow-sm bg-light">
                                  {detail.media ? (
                                    <div
                                      style={{
                                        maxHeight: "220px",
                                        overflow: "hidden",
                                        borderRadius: "10px",
                                        border: "1px solid #ddd",
                                      }}
                                      className="mb-3"
                                    >
                                      <img
                                        src={`${imageBaseApi}${detail?.media.url}`}
                                        alt={`Sub Detail ${index + 1}`}
                                        style={{
                                          width: "100%",
                                          objectFit: "cover",
                                        }}
                                        className="img-fluid hover-zoom"
                                      />
                                    </div>
                                  ) : (
                                    <div className="p-3 border rounded text-center bg-light">
                                      <i className="ri-image-add-line fs-2 text-secondary"></i>
                                      <h6 className="mt-2 mb-2 text-muted">
                                        No image selected
                                      </h6>
                                    </div>
                                  )}

                                  <Button
                                    outline
                                    color="secondary"
                                    size="sm"
                                    className="rounded-pill px-3 mt-2"
                                    onClick={() =>
                                      toggleMediaModal("subDetail", index)
                                    }
                                  >
                                    <i className="ri-image-add-line me-1"></i>{" "}
                                    {detail.media ? "Change" : "Choose"} Image
                                  </Button>

                                  {detail.media && (
                                    <Button
                                      outline
                                      color="danger"
                                      size="sm"
                                      className="rounded-pill px-3 mt-2 ms-2"
                                      onClick={() =>
                                        removeSubDetailMedia(index)
                                      }
                                    >
                                      <i className="ri-delete-bin-5-line me-1"></i>{" "}
                                      Remove
                                    </Button>
                                  )}
                                </div>
                                {errors.subDetails?.[index]?.thumbnailId &&
                                  touched.subDetails?.[index]?.thumbnailId && (
                                    <div className="text-danger small mt-1">
                                      {errors.subDetails[index].thumbnailId}
                                    </div>
                                  )}
                              </FormGroup>
                            </CardBody>
                          </Card>
                        ))}
                      </div>
                    </TabPane>
                  </TabContent>
                </CardBody>
              </Card>
            </Col>

            <Col lg={4}>
              {/* Content Image Card */}
              <Card>
                <CardBody>
                  <Label style={{ cursor: "pointer" }}>
                    Content Image <span className="text-danger">*</span>
                  </Label>

                  <div className="text-center p-3 border rounded shadow-sm bg-light">
                    {values.contentMedia ? (
                      <div
                        style={{
                          maxHeight: "220px",
                          overflow: "hidden",
                          borderRadius: "10px",
                          border: "1px solid #ddd",
                        }}
                        className="mb-3"
                      >
                        <img
                          src={`${imageBaseApi}${values.contentMedia.url}`}
                          alt="Content"
                          style={{ width: "100%", objectFit: "cover" }}
                          className="img-fluid hover-zoom"
                        />
                      </div>
                    ) : (
                      <div className="p-4 border rounded text-center bg-light">
                        <i className="ri-image-add-line fs-1 text-secondary"></i>
                        <h6 className="mt-2 mb-3 text-muted">
                          No content image selected
                        </h6>
                      </div>
                    )}

                    <div className="d-flex gap-2 justify-content-center mt-2">
                      <Button
                        size="sm"
                        outline
                        color="primary"
                        className="rounded-pill px-3"
                        onClick={() => toggleMediaModal("content")}
                      >
                        {values.contentMedia ? "Change" : "Choose"} Image
                      </Button>

                      {values.contentMedia && (
                        <Button
                          size="sm"
                          outline
                          color="danger"
                          className="rounded-pill px-3"
                          onClick={() => {
                            setFieldValue("contentImageId", "");
                            setFieldValue("contentMedia", null);
                          }}
                        >
                          <i className="ri-delete-bin-5-line me-1"></i> Remove
                        </Button>
                      )}
                    </div>
                  </div>
                  {errors.contentMedia && (
                    <div className="text-danger small mt-2 text-center">
                      {errors.contentMedia}
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </form>

        <MediaModal
          isOpen={mediaModalState.isOpen}
          toggle={toggleMediaModal}
          selectedMedia={null}
          setSelectedMedia={handleMediaSelect}
        />
      </Container>
    </div>
  );
};

export default AddEditWhoWeAre;
